import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData, Link, useSearchParams } from "@remix-run/react";
import { useState } from "react";
import { ArticleService } from "~/services/article.server";
import { Card } from "~/components/ui/Card";
import { Button } from "~/components/ui/Button";
import { LoadingSpinner } from "~/components/ui/LoadingSpinner";
import { ArticleCard } from "~/components/articles/ArticleCard";
import { SEOUtils } from "~/lib/seo-utils";

// Article category mapping
const CATEGORY_LABELS = {
  "slow-aging": "저속노화",
  "health-info": "건강정보",
  ingredients: "식재료정보",
  nutrition: "영양정보",
  lifestyle: "라이프스타일",
} as const;

const CATEGORIES = Object.keys(CATEGORY_LABELS) as Array<
  keyof typeof CATEGORY_LABELS
>;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = "건강정보 - 슬로우다이브";
  const description = "저속노화와 건강한 삶을 위한 유용한 정보를 확인하세요";

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const query = url.searchParams.get("query") || undefined;
  const category = url.searchParams.get("category") || undefined;
  const page = parseInt(url.searchParams.get("page") || "1", 10);
  const limit = 12; // Articles per page

  try {
    const result = await ArticleService.searchArticles({
      query,
      category,
      page,
      limit,
      publishedOnly: true,
    });

    return json({
      articles: result.articles,
      pagination: result.pagination,
      currentQuery: query,
      currentCategory: category,
    });
  } catch (error) {
    console.error("Failed to load articles:", error);
    throw new Response("Failed to load articles", { status: 500 });
  }
}

export default function ArticlesIndex() {
  const { articles, pagination, currentQuery, currentCategory } =
    useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(currentQuery || "");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);

    if (searchQuery.trim()) {
      newParams.set("query", searchQuery.trim());
    } else {
      newParams.delete("query");
    }

    newParams.delete("page"); // Reset to first page
    setSearchParams(newParams);
  };

  const handleCategoryFilter = (category: string | null) => {
    const newParams = new URLSearchParams(searchParams);

    if (category) {
      newParams.set("category", category);
    } else {
      newParams.delete("category");
    }

    newParams.delete("page"); // Reset to first page
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            건강정보
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            저속노화와 건강한 삶을 위한 유용한 정보를 확인하세요
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="아티클 검색..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <Button type="submit" variant="primary">
                검색
              </Button>
            </div>
          </form>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={currentCategory ? "secondary" : "primary"}
              size="sm"
              onClick={() => handleCategoryFilter(null)}
            >
              전체
            </Button>
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                variant={currentCategory === category ? "primary" : "secondary"}
                size="sm"
                onClick={() => handleCategoryFilter(category)}
              >
                {CATEGORY_LABELS[category]}
              </Button>
            ))}
          </div>
        </div>

        {/* Results Info */}
        <div className="mb-6">
          <p className="text-gray-600">
            총 {pagination.total}개의 아티클
            {currentQuery && ` (검색어: "${currentQuery}")`}
            {currentCategory &&
              ` (카테고리: ${
                CATEGORY_LABELS[currentCategory as keyof typeof CATEGORY_LABELS]
              })`}
          </p>
        </div>

        {/* Articles Grid */}
        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                categoryLabel={
                  CATEGORY_LABELS[
                    article.category as keyof typeof CATEGORY_LABELS
                  ]
                }
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              아티클을 찾을 수 없습니다
            </h3>
            <p className="text-gray-600 mb-4">
              검색 조건을 변경하거나 다른 카테고리를 선택해보세요.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setSearchQuery("");
                setSearchParams({});
              }}
            >
              전체 아티클 보기
            </Button>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              이전
            </Button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((page) => {
                const current = pagination.page;
                return (
                  page === 1 ||
                  page === pagination.totalPages ||
                  (page >= current - 2 && page <= current + 2)
                );
              })
              .map((page, index, array) => {
                const prevPage = array[index - 1];
                const showEllipsis = prevPage && page - prevPage > 1;

                return (
                  <div key={page} className="flex items-center">
                    {showEllipsis && (
                      <span className="px-2 text-gray-500">...</span>
                    )}
                    <Button
                      variant={
                        pagination.page === page ? "primary" : "secondary"
                      }
                      size="sm"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  </div>
                );
              })}

            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              다음
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
